import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Tooltip } from '../components/ui/Tooltip';
import { Plus, Filter, Download, Eye, Edit, Search, MapPin, Building, X, ChevronRight, ChevronLeft, Save, CheckCircle2, AlertTriangle, Trash2, AlertCircle, List, LayoutGrid } from 'lucide-react';
import { useRBAC } from '../contexts/RBACContext';
import { PermissionGate } from '../components/PermissionGate';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { MultiSelect } from '../components/ui/MultiSelect';
import { Textarea } from '../components/ui/Textarea';
import { useNavigate } from 'react-router-dom';
import { mockData } from '../services/mockData';
import { motion, AnimatePresence } from 'framer-motion';
// Mock data service would be imported
const initialClientsData = mockData.getClients();
const columnsConfig = [{
  key: 'number',
  label: 'Codice'
}, {
  key: 'name',
  label: 'Ragione sociale'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'zone',
  label: 'Zona'
}, {
  key: 'clientOf',
  label: 'Cliente di'
}, {
  key: 'safetyReferent',
  label: 'Ref. sicurezza'
}, {
  key: 'employeesCount',
  label: 'Dipendenti'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function ClientsPage() {
  const navigate = useNavigate();
  const {
    canCreate,
    canEdit,
    canDelete,
    currentUser
  } = useRBAC();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<any>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [viewMode, setViewMode] = useState<'principale' | 'pending'>('principale');
  // Wizard State
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<any>({});
  // Hooks
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialClientsData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult,
    isSearching
  } = useSearch(filterResult, ['name', 'number', 'email', 'vatNumber']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleEdit = (client: any) => {
    setEditingClient(client);
    setFormData(client);
    setCurrentStep(1);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setEditingClient(null);
    setFormData({
      status: 'prospect',
      clientOf: [],
      zone: '',
      addresses: [],
      number: '' // Will be pre-filled by ProgressiveNumberInput
    });
    setCurrentStep(1);
    setIsModalOpen(true);
  };
  const handleSave = () => {
    // Logic to save client
    console.log('Saving client:', formData);
    setSaveSuccess(true);
    // Show success animation then close
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
    }, 1500);
  };
  const handleDeleteClick = (e: React.MouseEvent, client: any) => {
    e.stopPropagation();
    setClientToDelete(client);
    setShowDeleteDialog(true);
  };
  const handleConfirmDelete = () => {
    console.log('Deleting client:', clientToDelete);
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setShowDeleteDialog(false);
      setClientToDelete(null);
    }, 1500);
  };
  const hasActiveFilters = Object.keys(filters).some(key => filters[key]);
  const renderWizardStep = () => {
    switch (currentStep) {
      case 1:
        // Dati Principali
        return <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-3295" data-mp="3p281hvf42lgd">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3296" data-mp="3tyygeff6jb07">
              <Input label="Denominazione / Ragione sociale" placeholder="Es. Azienda SRL" value={formData.name || ''} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} required className="col-span-full" data-id="element-3297" data-mp="1un5fi7p9tli7" />

              <ProgressiveNumberInput type="client" label="Numero cliente" value={formData.number} onChange={val => setFormData({
              ...formData,
              number: val
            })} disabled={false} data-id="element-3298" data-mp="7zln1ohghg5q" />

              <div className="space-y-1" data-id="element-3299" data-mp="qorzitrnpbj3">
                <Select label="Stato anagrafica" value={formData.status || 'prospect'} onChange={e => setFormData({
                ...formData,
                status: e.target.value
              })} options={[{
                value: 'prospect',
                label: 'Prospect'
              }, {
                value: 'active',
                label: 'Attivo'
              }, {
                value: 'inactive',
                label: 'Non attivo'
              }, {
                value: 'draft',
                label: 'Bozza'
              }]} required disabled={!canEdit('clients', 'status')} data-id="element-3300" data-mp="qngukqmh5xqe" />
                {formData.status === 'inactive' && <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200 flex items-center gap-1" data-id="element-3301" data-mp="2g42px0lqmadh">
                    <AlertTriangle className="h-3 w-3" data-id="element-3302" data-mp="267g3i7drjb9c" />
                    Attenzione: Automazioni disabilitate
                  </div>}
              </div>

              <div className="space-y-2" data-id="element-3303" data-mp="2b8x21w8rylmr">
                <label className="block text-sm font-medium text-slate-700" data-id="element-3304" data-mp="1rkhhwt1l7b2j">
                  Cliente di
                </label>
                <div className="flex items-center gap-6 pt-2" data-id="element-3305" data-mp="1mo0r1r306ilr">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-3306" data-mp="21jqg5y3hjbup">
                    <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={(formData.clientOf || []).includes('sef_studio')} onChange={e => {
                    const current = formData.clientOf || [];
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        clientOf: [...current, 'sef_studio']
                      });
                    } else {
                      setFormData({
                        ...formData,
                        clientOf: current.filter((v: string) => v !== 'sef_studio')
                      });
                    }
                  }} data-id="element-3307" data-mp="1wkn9683y2vk3" />
                    <span className="text-sm text-slate-700" data-id="element-3308" data-mp="9i3p4omvxyos">Sef Studio</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-3309" data-mp="dhueqp0oe8rq">
                    <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={(formData.clientOf || []).includes('seform')} onChange={e => {
                    const current = formData.clientOf || [];
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        clientOf: [...current, 'seform']
                      });
                    } else {
                      setFormData({
                        ...formData,
                        clientOf: current.filter((v: string) => v !== 'seform')
                      });
                    }
                  }} data-id="element-3310" data-mp="3cykc5zvub4ua" />
                    <span className="text-sm text-slate-700" data-id="element-3311" data-mp="2cp1fp6e14q78">Seform</span>
                  </label>
                </div>
              </div>
            </div>
          </div>;
      case 2:
        // Indirizzi
        return <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-3312" data-mp="1y0er0e2iim3c">
            <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider border-b border-slate-100 pb-2" data-id="element-3313" data-mp="1tyth91l9ynxd">
              Indirizzo operativo
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3314" data-mp="1oyyo0re11m95">
              <Input label="Via" value={formData.address?.street || ''} onChange={e => setFormData({
              ...formData,
              address: {
                ...formData.address,
                street: e.target.value
              }
            })} className="col-span-full" data-id="element-3315" data-mp="1k1ot4w7vcidx" />
              <Input label="Civico" value={formData.address?.number || ''} onChange={e => setFormData({
              ...formData,
              address: {
                ...formData.address,
                number: e.target.value
              }
            })} data-id="element-3316" data-mp="17s6lhwlckrn6" />
              <Input label="CAP" value={formData.address?.zip || ''} onChange={e => setFormData({
              ...formData,
              address: {
                ...formData.address,
                zip: e.target.value
              }
            })} data-id="element-3317" data-mp="p1yqy2vpr9il" />
              <Input label="Comune" value={formData.address?.city || ''} onChange={e => setFormData({
              ...formData,
              address: {
                ...formData.address,
                city: e.target.value
              }
            })} data-id="element-3318" data-mp="3n9e64w2im08s" />
              <Input label="Provincia (2 lettere)" value={formData.address?.province || ''} onChange={e => setFormData({
              ...formData,
              address: {
                ...formData.address,
                province: e.target.value
              }
            })} maxLength={2} data-id="element-3319" data-mp="9yotp5ztth9c" />
            </div>

            <Select label="Zona" value={formData.zone || ''} onChange={e => setFormData({
            ...formData,
            zone: e.target.value
          })} options={[{
            value: 'arezzo',
            label: 'Arezzo'
          }, {
            value: 'valdarno',
            label: 'Valdarno'
          }, {
            value: 'casentino',
            label: 'Casentino'
          }, {
            value: 'valdichiana',
            label: 'Valdichiana'
          }, {
            value: 'valtiberina',
            label: 'Valtiberina'
          }, {
            value: 'firenze',
            label: 'Firenze'
          }, {
            value: 'siena',
            label: 'Siena'
          }, {
            value: 'perugia',
            label: 'Perugia'
          }]} required data-id="element-3320" data-mp="13f8unsrwmhgw" />

            <div className="flex items-center gap-2 py-2" data-id="element-3321" data-mp="2he2c2rsemsin">
              <input type="checkbox" id="sameAddress" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={formData.sameLegalAddress} onChange={e => setFormData({
              ...formData,
              sameLegalAddress: e.target.checked
            })} data-id="element-3322" data-mp="2me6wgswfo7e5" />
              <label htmlFor="sameAddress" className="text-sm text-slate-700" data-id="element-3323" data-mp="23ignumtzwcra">
                Sede legale uguale a indirizzo operativo
              </label>
            </div>

            {!formData.sameLegalAddress && <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-id="element-3324" data-mp="3coz64l7kx61j">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4" data-id="element-3325" data-mp="3cmw9hibx8enu">
                  Sede legale
                </h4>
                <Textarea label="Indirizzo completo" value={formData.legalAddress || ''} onChange={e => setFormData({
              ...formData,
              legalAddress: e.target.value
            })} data-id="element-3326" data-mp="3mg3hzc1ykas5" />
              </div>}
          </div>;
      case 3:
        // Contatti
        return <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-3327" data-mp="965tcammkl8f">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3328" data-mp="47m8yenojhi6">
              <Input label="Telefono aziendale" type="tel" value={formData.phone || ''} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} data-id="element-3329" data-mp="2b0ewkp4crjtg" />
              <Input label="Email amministrativa" type="email" value={formData.emailAdmin || ''} onChange={e => setFormData({
              ...formData,
              emailAdmin: e.target.value
            })} required data-id="element-3330" data-mp="qjox1r3mvsau" />
              <Input label="Email consulenza" type="email" value={formData.emailConsulting || ''} onChange={e => setFormData({
              ...formData,
              emailConsulting: e.target.value
            })} data-id="element-3331" data-mp="7sxpmv37ibz3" />
              <Input label="Email formazione" type="email" value={formData.emailTraining || ''} onChange={e => setFormData({
              ...formData,
              emailTraining: e.target.value
            })} data-id="element-3332" data-mp="1s7pzl1ir0ajo" />
              <Input label="PEC" type="email" value={formData.pec || ''} onChange={e => setFormData({
              ...formData,
              pec: e.target.value
            })} data-id="element-3333" data-mp="2s3qbdvh9kuo4" />
              <Input label="Sito internet" type="url" value={formData.website || ''} onChange={e => setFormData({
              ...formData,
              website: e.target.value
            })} data-id="element-3334" data-mp="3fv5fntslpm20" />
            </div>
            <div className="h-px bg-slate-100 my-4" data-id="element-3335" data-mp="2m92uz23yn12"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3336" data-mp="2ko8b9fsn437">
              <Input label="Persona di riferimento" value={formData.contactPerson || ''} onChange={e => setFormData({
              ...formData,
              contactPerson: e.target.value
            })} data-id="element-3337" data-mp="9z8ea2dkrzxe" />
              <Input label="Cellulare referente" type="tel" value={formData.contactPhone || ''} onChange={e => setFormData({
              ...formData,
              contactPhone: e.target.value
            })} data-id="element-3338" data-mp="nv3pdm1qg6q8" />
            </div>
          </div>;
      case 4:
        // Dati Fiscali
        return <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-3339" data-mp="1bnsmnv52fg3w">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3340" data-mp="1glkl7807u0h2">
              <Input label="Partita IVA" value={formData.vatNumber || ''} onChange={e => setFormData({
              ...formData,
              vatNumber: e.target.value
            })} maxLength={11} data-id="element-3341" data-mp="1gjrjm74gm6fx" />
              <Input label="Codice fiscale" value={formData.fiscalCode || ''} onChange={e => setFormData({
              ...formData,
              fiscalCode: e.target.value
            })} maxLength={16} data-id="element-3342" data-mp="26vvudywvig44" />
              <Input label="Codice SDI" value={formData.sdiCode || ''} onChange={e => setFormData({
              ...formData,
              sdiCode: e.target.value
            })} maxLength={7} data-id="element-3343" data-mp="1syh0agvodk5t" />
              <Input label="Codice Ateco" value={formData.atecoCode || ''} onChange={e => setFormData({
              ...formData,
              atecoCode: e.target.value
            })} data-id="element-3344" data-mp="374ltgi1m7hlp" />
            </div>
            <Textarea label="Attività aziendale" value={formData.activityDescription || ''} onChange={e => setFormData({
            ...formData,
            activityDescription: e.target.value
          })} data-id="element-3345" data-mp="324hcys0pimvh" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3346" data-mp="3gtdh4dzn7fqt">
              <Input label="Legale rappresentante" value={formData.legalRep || ''} onChange={e => setFormData({
              ...formData,
              legalRep: e.target.value
            })} data-id="element-3347" data-mp="3kv7cc6r2jrjl" />
              <Input label="Datore di lavoro" value={formData.employer || ''} onChange={e => setFormData({
              ...formData,
              employer: e.target.value
            })} data-id="element-3348" data-mp="1bplbvl0shtmu" />
            </div>
          </div>;
      case 5:
        // Relazioni
        return <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-3349" data-mp="s0t4w34w7fdj">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3350" data-mp="wsubb8fdqbvn">
              <Select label="Provenienza cliente" value={formData.source || ''} onChange={e => setFormData({
              ...formData,
              source: e.target.value
            })} options={[{
              value: 'Web',
              label: 'Web'
            }, {
              value: 'Passaparola diretto',
              label: 'Passaparola diretto'
            }, {
              value: 'Partner',
              label: 'Partner'
            }, {
              value: 'Commercialista',
              label: 'Commercialista'
            }, {
              value: 'Altro',
              label: 'Altro'
            }]} data-id="element-3351" data-mp="j1651lrtahml" />
              <Select label="Referente interno" value={formData.safetyReferent || ''} onChange={e => setFormData({
              ...formData,
              safetyReferent: e.target.value
            })} options={[{
              value: 'alessio',
              label: 'Alessio'
            }, {
              value: 'gianfranco',
              label: 'Gian Franco'
            }, {
              value: 'lorenzo',
              label: 'Lorenzo'
            }, {
              value: 'tiziano',
              label: 'Tiziano'
            }]} required disabled={!canEdit('clients', 'safetyReferent')} data-id="element-3352" data-mp="9dbmshfvpky7" />
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex justify-between items-center" data-id="element-3353" data-mp="1mwawcllr0f2w">
              <div data-id="element-3354" data-mp="1rwyppdxg980a">
                <h4 className="font-bold text-seform-navy" data-id="element-3355" data-mp="1rwgi5l5c77d2">
                  Dipendenti censiti
                </h4>
                <p className="text-sm text-slate-500" data-id="element-3356" data-mp="1wsxgt9a5x44e">
                  Calcolato automaticamente dal modulo Risorse umane
                </p>
              </div>
              <div className="text-3xl font-bold text-seform-orange" data-id="element-3357" data-mp="2i217n0k8os4">
                {formData.employeesCount || 0}
              </div>
            </div>
          </div>;
    }
  };
  return <Layout data-id="element-3358" data-mp="3mb44d289onm6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-3359" data-mp="3ma2hm0hw2com">
        <div data-id="element-3360" data-mp="3haybjiq9gv2i">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-3361" data-mp="3cdzi72ef8s5d">
            Anagrafiche clienti
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-3362" data-mp="dw27r0y67lfk">
            Gestione completa delle aziende clienti e prospect.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-3363" data-mp="8ykaw8xfgwnx">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-3364" data-mp="3y5ykeosn0k3">
            <button onClick={() => setViewMode('principale')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'principale' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3365" data-mp="40909vzb6i4n">
              Principale
            </button>
            <button onClick={() => setViewMode('pending')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'pending' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3366" data-mp="cedgbcutmcjx">
              Pending
            </button>
          </div>
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-3368" data-mp="v5mq7n8i73c1" />} data-id="element-3367" data-mp="7fse57w0disu">
            Esporta
          </Button>
          <PermissionGate module="clients" action="create" data-id="element-3369" data-mp="103y0u06mhhvb">
            <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-3371" data-mp="l65mhbctazip" />} data-id="element-3370" data-mp="101bq5rofqm8v">
              Nuova anagrafica
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters & Search */}
      <Card className="mb-6" noPadding data-id="element-3372" data-mp="35v33s80cydih">
        <div className="p-4 flex flex-col gap-4" data-id="element-3373" data-mp="3avprfppgv6ve">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-3374" data-mp="3awio1xupofzt">
            <div className="flex-1" data-id="element-3375" data-mp="1sxujjnj9f3ip">
              <Input placeholder="Cerca per ragione sociale, P.IVA, referente..." startContent={<Search className="h-4 w-4" data-id="element-3377" data-mp="1nyrmoqdfo3lt" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-3376" data-mp="1swt7bfqicaze" />
            </div>
            <div className="flex gap-2" data-id="element-3378" data-mp="1xxyvr1d4xc34">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-3380" data-mp="1izofaz9zdlao" />} data-id="element-3379" data-mp="1a73ayo73jh5d">
                Filtri{' '}
                {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-3381" data-mp="1e85c5akkq39z">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="clients" lockedColumns={['name']} data-id="element-3382" data-mp="mh902c6c82nr" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-3383" data-mp="2veoiz7nub3dg">
              <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti gli stati'
          }, {
            value: 'active',
            label: 'Attivo'
          }, {
            value: 'prospect',
            label: 'Prospect'
          }, {
            value: 'inactive',
            label: 'Non attivo'
          }, {
            value: 'draft',
            label: 'Bozza'
          }]} data-id="element-3384" data-mp="22oprl5m6au5m" />
              <Select label="Zona" value={filters.zone || ''} onChange={e => setFilter('zone', e.target.value)} options={[{
            value: '',
            label: 'Tutte le zone'
          }, {
            value: 'arezzo',
            label: 'Arezzo'
          }, {
            value: 'valdarno',
            label: 'Valdarno'
          }, {
            value: 'casentino',
            label: 'Casentino'
          }, {
            value: 'firenze',
            label: 'Firenze'
          }]} data-id="element-3385" data-mp="17lw5nrdv04s7" />
              <div className="col-span-full flex justify-end" data-id="element-3386" data-mp="1myf9j22946m6">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-3388" data-mp="1i76zq4x0ej8h" />} className="font-medium" data-id="element-3387" data-mp="1rx9r60ohfp4p">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      {/* Data Table */}
      <Card noPadding className="overflow-hidden" data-id="element-3389" data-mp="1983fuyq0pzsx">
        <div className="overflow-x-auto" data-id="element-3390" data-mp="u9hur8x90ry6">
          <table className="w-full text-left border-collapse table-auto" data-id="element-3391" data-mp="uasv0yrhjhkj">
            <thead data-id="element-3392" data-mp="z7jdttuocr9n">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-3393" data-mp="z8upsgy9x5bu">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 ${col.key === 'actions' ? 'text-left pr-6' : ''} ${col.key === 'number' ? 'w-24' : ''}`} data-id="element-3394" data-mp="hy8ojw3yyggr">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-3395" data-mp="mwiki8xipl3f">
              {isSearching ? <tr data-id="element-3396" data-mp="10o6ti3cgztg6">
                  <td colSpan={visibleColumns.length} className="px-4 py-12 text-center text-slate-500" data-id="element-3397" data-mp="10nosktev4r1x">
                    <div className="flex justify-center items-center gap-2" data-id="element-3398" data-mp="1ammm6ar5bars">
                      <div className="animate-spin h-5 w-5 border-2 border-seform-orange border-t-transparent rounded-full" data-id="element-3399" data-mp="15o30mbggs0zy"></div>
                      Ricerca in corso...
                    </div>
                  </td>
                </tr> : paginatedData.length > 0 ? paginatedData.map(client => <tr key={client.id} className="hover:bg-slate-50/80 transition-colors group cursor-pointer" onClick={() => navigate(`/clients/${client.id}`)} data-id="element-3400" data-mp="2gcv5m7bah3h7">
                    {visibleColumns.includes('number') && <td className="px-4 py-4 text-sm font-mono text-slate-500" data-id="element-3401" data-mp="22m8f32fomjw7">
                        {client.number}
                      </td>}
                    {visibleColumns.includes('name') && <td className="px-4 py-4" data-id="element-3402" data-mp="1sqwxmccw2ykr">
                        <div data-id="element-3403" data-mp="1oo09650o488w">
                          <p className="text-xs font-semibold text-slate-900" data-id="element-3404" data-mp="1on8qfus66501">
                            {client.name}
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-3405" data-mp="3j1jcjvhdql24">
                            {client.email}
                          </p>
                        </div>
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-3406" data-mp="3qkixi5khoknl">
                        <div className="flex items-center gap-2" data-id="element-3407" data-mp="3qj7sy3j7z8vf">
                          {viewMode === 'pending' ? <Badge variant="warning" data-id="element-3408" data-mp="3vdc3ewrp1z19">Pending</Badge> : <Badge variant={client.status === 'Attivo' ? 'success' : client.status === 'Prospect' ? 'info' : 'neutral'} data-id="element-3409" data-mp="470vcgo9ny0f">
                              {client.status}
                            </Badge>}
                        </div>
                      </td>}
                    {visibleColumns.includes('zone') && <td className="px-4 py-4" data-id="element-3410" data-mp="2sw8h3xxd8851">
                        <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-3411" data-mp="2sux7vil5i86j">
                          <MapPin className="h-3.5 w-3.5 text-slate-400" data-id="element-3412" data-mp="2nxds2e8t1nch" />
                          {client.zone}
                        </div>
                      </td>}
                    {visibleColumns.includes('clientOf') && <td className="px-4 py-4" data-id="element-3413" data-mp="298956jr2euy1">
                        <div className="flex gap-1 flex-wrap" data-id="element-3414" data-mp="2990w3h9pw4hz">
                          {client.clientOf?.map((s: string) => <span key={s} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200 capitalize" data-id="element-3415" data-mp="2iyal2o99l71v">
                              {s.replace('_', ' ')}
                            </span>)}
                        </div>
                      </td>}
                    {visibleColumns.includes('safetyReferent') && <td className="px-4 py-4 text-sm text-slate-600 capitalize" data-id="element-3416" data-mp="5rm4oi4i8opo">
                        {client.safetyReferent}
                      </td>}
                    {visibleColumns.includes('employeesCount') && <td className="px-4 py-4 text-sm text-slate-600 font-mono" data-id="element-3417" data-mp="3t5ga371arbrd">
                        {client.employeesCount}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" onClick={e => e.stopPropagation()} data-id="element-3418" data-mp="aqxmlugly08u">
                        <div className="flex items-center gap-1" data-id="element-3419" data-mp="fo78x18p9vsg">
                          <Tooltip content="Visualizza cliente" data-id="element-3420" data-mp="2vgwphpispj4l">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => navigate(`/clients/${client.id}${viewMode === 'pending' ? '?pending=true' : ''}`)} data-id="element-3421" data-mp="30g9fgj4yr6be">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-3422" data-mp="3202n9onef6o7" />
                            </Button>
                          </Tooltip>
                          <PermissionGate module="clients" action="edit" data-id="element-3423" data-mp="3ungagv1aonv4">
                            <Tooltip content="Modifica cliente" data-id="element-3424" data-mp="3ul2fsnaj1do3">
                              <Button variant="light" size="sm" className="!px-1" onClick={() => navigate(`/clients/${client.id}?edit=true${viewMode === 'pending' ? '&pending=true' : ''}`)} data-id="element-3425" data-mp="3pmsk8bkw4b1y">
                                <Edit className="h-4 w-4 text-seform-orange" data-id="element-3426" data-mp="2461mi6u7gi8h" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                          <PermissionGate module="clients" action="delete" data-id="element-3427" data-mp="2dvuxb5miwtbo">
                            <Tooltip content="Elimina cliente" data-id="element-3428" data-mp="2hwwnyy2djx21">
                              <Button variant="light" size="sm" className="!px-1" onClick={e => handleDeleteClick(e, client)} data-id="element-3429" data-mp="2hyhi4mks1dzd">
                                <Trash2 className="h-4 w-4 text-red-500" data-id="element-3430" data-mp="2wsw3gtrtczh7" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                        </div>
                      </td>}
                  </tr>) : <tr data-id="element-3431" data-mp="pfvin5vptlqn">
                  <td colSpan={visibleColumns.length} className="px-4 py-12 text-center text-slate-500" data-id="element-3432" data-mp="pgdp0vc3dgca">
                    Nessun cliente trovato
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        <div className="border-t border-slate-100" data-id="element-3433" data-mp="1dcrg2yop0jhr">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-3434" data-mp="1dbge94et5lta" />
        </div>
      </Card>

      {/* Create/Edit Wizard Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingClient ? 'Modifica anagrafica' : 'Nuova anagrafica'} size="2xl" data-id="element-3435" data-mp="1w40yjxgfyilh">
        <AnimatePresence mode="wait" data-id="element-3436" data-mp="2ar952kyt1zr4">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} className="flex flex-col items-center justify-center py-16" data-id="element-3437" data-mp="2aowq8gocg5ia">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-3438" data-mp="1ryezuiqzhm03">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-3439" data-mp="1e19w4peqsz80" />
              </motion.div>
              <motion.p initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg font-semibold text-slate-800" data-id="element-3440" data-mp="1tj9vyufg45ck">
                Anagrafica salvata con successo
              </motion.p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="flex flex-col h-full" data-id="element-3441" data-mp="15snnx35fyl2k">
              {/* Stepper */}
              <div className="mb-8 px-2" data-id="element-3442" data-mp="wqo8gfa2rp1q">
                <div className="flex items-center justify-between relative" data-id="element-3443" data-mp="rscuptgnsue8">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-100 -z-10" data-id="element-3444" data-mp="mtrv9w98eequ"></div>
                  {[1, 2, 3, 4, 5].map(step => <div key={step} className={`
                        w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors
                        ${step === currentStep ? 'bg-seform-orange text-white shadow-lg scale-110' : step < currentStep ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'}
                      `} data-id="element-3445" data-mp="2yjrel44xecas">
                      {step < currentStep ? <CheckCircle2 className="h-5 w-5" data-id="element-3446" data-mp="2kqi6r7wmlyud" /> : step}
                    </div>)}
                </div>
                <div className="flex justify-between mt-2 text-xs font-medium text-slate-500 px-1" data-id="element-3447" data-mp="2tkohc5v6tadd">
                  <span data-id="element-3448" data-mp="1wyl70f6gax9m">Dati base</span>
                  <span data-id="element-3449" data-mp="211r878ephy98">Indirizzi</span>
                  <span data-id="element-3450" data-mp="210pz39h93lu5">Contatti</span>
                  <span data-id="element-3451" data-mp="gk74i5pauzdg">Fiscali</span>
                  <span data-id="element-3452" data-mp="gl8fyba89nvp">Relazioni</span>
                </div>
              </div>

              {/* Step Content */}
              <div className="flex-1 overflow-y-auto custom-scrollbar px-1 py-2" data-id="element-3453" data-mp="kmbu8tcmnbut">
                {renderWizardStep()}
              </div>

              {/* Footer Actions */}
              <div className="flex justify-between items-center pt-6 mt-6 border-t border-slate-100" data-id="element-3454" data-mp="ubvh0a3gnpuy">
                <Button variant="flat" color="default" onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : setIsModalOpen(false)} startContent={currentStep > 1 ? <ChevronLeft className="h-4 w-4" data-id="element-3456" data-mp="3a2rdohdxmwh5" /> : undefined} data-id="element-3455" data-mp="z9n5eu4wctue">
                  {currentStep > 1 ? 'Indietro' : 'Annulla'}
                </Button>

                <div className="flex gap-3" data-id="element-3457" data-mp="37i1s6u3kyjn3">
                  <Button variant="light" size="sm" className="text-slate-400" data-id="element-3458" data-mp="37jwdi9irkdrp">
                    Salva bozza
                  </Button>
                  {currentStep < 5 ? <Button onClick={() => setCurrentStep(currentStep + 1)} endContent={<ChevronRight className="h-4 w-4" data-id="element-3460" data-mp="2suqdwkg0klja" />} data-id="element-3459" data-mp="2xtjvanyf8woq">
                      Avanti
                    </Button> : <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-3462" data-mp="3gizun7ydej3k" />} color="success" data-id="element-3461" data-mp="3licle3sd4abf">
                      Completa e salva
                    </Button>}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Conferma eliminazione" size="sm" data-id="element-3463" data-mp="211g6dc36r6oz">
        <AnimatePresence mode="wait" data-id="element-3464" data-mp="27j1bmj8bm571">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-3465" data-mp="27g5douwh3gil">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-3466" data-mp="19ur8u20pndev">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-3467" data-mp="m7m7sw9a0369" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-3468" data-mp="ha3wx0bp7dvm">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-3469" data-mp="q8o6nbza6d7p">
              <div className="space-y-6" data-id="element-3470" data-mp="1062ewob1vvox">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-3471" data-mp="10511id6kax41">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-3472" data-mp="1e0mmm99ocwr6" />
                  <div data-id="element-3473" data-mp="19xgkndtlhrqi">
                    <p className="text-sm text-red-700 font-medium" data-id="element-3474" data-mp="19z1botgpasjm">
                      Attenzione: questa azione è irreversibile.
                    </p>
                    <p className="text-sm text-red-600 mt-1" data-id="element-3475" data-mp="1iyo4wrza0s1l">
                      L'anagrafica <strong data-id="element-3476" data-mp="2309afv5t8mqy">{clientToDelete?.name}</strong> verrà
                      eliminata definitivamente insieme a tutti i dati
                      associati.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-3477" data-mp="2bskzbcla024t">
                  <Button variant="flat" color="default" onClick={() => setShowDeleteDialog(false)} data-id="element-3478" data-mp="2grnr3vc8wbuu">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={handleConfirmDelete} startContent={<Trash2 className="h-4 w-4" data-id="element-3480" data-mp="1t3wgqxa9m8w7" />} data-id="element-3479" data-mp="1o2yyylt63gv8">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}